import { useEffect, useState } from "react";
import { usePrices } from "@/lib/prices-context";

/**
 * The hero's right-rail focal element: the live raw 40-byte signed message
 * for BTC/USD, rendered as a wire-format readout. Replaces the previous
 * spinning dial — calm by design. The bytes change every 500ms when a new
 * round lands, but only the price/timestamp/round segments shift; the tag
 * stays constant. No constant rotation, no looping animation.
 *
 * The leading dot pulses briefly on each round to signal "this is alive"
 * without forcing the eye to chase moving geometry.
 *
 * Color segmentation mirrors the Asset Terminal so the visual vocabulary
 * is consistent across the page.
 */
export function LatestSignature() {
  const { assets, lastRound } = usePrices();
  const entry = assets?.["BTC/USD"];
  const [pulseKey, setPulseKey] = useState(0);
  const [ageS, setAgeS] = useState<number | null>(null);

  // Trigger the leading-dot pulse when a new round lands.
  useEffect(() => {
    if (lastRound != null) setPulseKey((k) => k + 1);
  }, [lastRound]);

  // Tick the "signed Xs ago" caption at ~5 Hz.
  useEffect(() => {
    if (!entry) {
      setAgeS(null);
      return;
    }
    const tick = () => setAgeS(Math.max(0, Date.now() / 1000 - entry.timestamp));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [entry?.timestamp]);

  const message = entry?.message ?? "";
  const tag = message.slice(0, 16);
  const price = message.slice(16, 48);
  const time = message.slice(48, 64);
  const round = message.slice(64, 80);

  return (
    <div className="relative w-full max-w-[440px] rounded-xl border border-border bg-surface/60 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.24em] text-m55">
          <span className="relative inline-flex size-1.5">
            <span className="absolute inset-0 rounded-full bg-emerald" />
            <span
              key={pulseKey}
              className="absolute -inset-1 rounded-full border border-emerald/70"
              style={{ animation: "ls-ping 800ms ease-out both" }}
            />
          </span>
          LATEST SIGNATURE
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-m45">
          BTC<span className="text-m30">/USD</span>
        </span>
      </div>

      {/* Bytes — color-segmented by protocol field */}
      <div className="px-5 py-5">
        <div className="font-mono text-[13px] leading-[1.7] tabular break-all tracking-[0.01em]">
          {entry ? (
            <>
              <span className="text-emerald">{tag}</span>
              <span className="text-amber">{price}</span>
              <span className="text-m55">{time}</span>
              <span className="text-emerald">{round}</span>
            </>
          ) : (
            <span className="text-m30">awaiting first round…</span>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[9px] uppercase tracking-[0.22em]">
          <Legend color="bg-emerald" label="ASSET TAG" />
          <Legend color="bg-amber" label="PRICE i128" />
          <Legend color="bg-m55" label="TIMESTAMP" />
          <Legend color="bg-emerald" label="ROUND" />
        </div>
      </div>

      {/* Footer — round id + age + see-more affordance */}
      <div className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
        <div className="font-mono text-[10.5px] tabular text-m70">
          <span className="text-m45">R</span>
          {entry ? entry.round_id.toLocaleString("en-US") : "—"}
          <span className="text-m30 mx-2">·</span>
          <span className="text-m45">signed</span>{" "}
          <span className="text-text">
            {ageS == null ? "—" : ageS.toFixed(1) + "s"}
          </span>{" "}
          <span className="text-m45">ago</span>
        </div>
      </div>

      <a
        href="#terminal"
        className="group flex items-center justify-center gap-2 border-t border-border px-5 py-2.5 font-mono text-[9.5px] uppercase tracking-[0.24em] text-m45 transition-colors hover:bg-bg-deep/40 hover:text-emerald"
      >
        See all 7 feeds in the terminal
        <span className="transition-transform group-hover:translate-y-0.5">↓</span>
      </a>

      <style>{`
        @keyframes ls-ping {
          0%   { opacity: 1; transform: scale(0.6); }
          80%  { opacity: 0; transform: scale(2.4); }
          100% { opacity: 0; transform: scale(2.4); }
        }
      `}</style>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-m40">
      <span className={`inline-block size-[7px] rounded-[1px] ${color}`} />
      {label}
    </span>
  );
}
